import { HelpAnchor } from "./anchors";

export type HelpPlacement = "top" | "bottom" | "left" | "right";

export interface TourStep {
  anchor: HelpAnchor;
  title: string;
  body: string;
  /** A preference: the callout flips when there is no room. */
  placement?: HelpPlacement;
  /**
   * Runs, and is awaited, before the anchor is looked up: e.g. to open the
   * drawer on phones and wait for it to slide in.
   */
  before?: () => void | Promise<void>;
  /**
   * When the anchor is still missing after a short wait: "skip" moves to the
   * next step (default), "center" shows the step in the middle of the page.
   */
  fallback?: "center" | "skip";
}

export interface HintDefinition {
  /** A react-router path pattern, e.g. "/eventdevice/:id". */
  route: string;
  anchor: HelpAnchor;
  body: string;
  title?: string;
  placement?: HelpPlacement;
}

export interface AnnouncementDefinition {
  title: string;
  body: string;
  /** Deploy date, `YYYY-MM-DD`. Only users who joined before it see the dialog. */
  released: string;
  /** For a feature behind a feature flag: show only once the flag is on. */
  requires?: { capability: string };
  /**
   * Open the dialog only on this page (a react-router path pattern), for a
   * feature that makes sense only where it lives. Anywhere when omitted.
   */
  route?: string;
  /** Has a video, found by the announcement id (see helpMedia). */
  video?: boolean;
  /** Callouts that "Show me" walks through after the dialog. */
  tour?: TourStep[];
  /** One-off callouts, shown the first time their anchor is on their route. */
  hints?: HintDefinition[];
}

export interface Announcement extends AnnouncementDefinition {
  /** Lowercase letters, digits and dashes; it names the media folder too. */
  id: string;
}

export const announcement = (
  id: string,
  definition: AnnouncementDefinition
): Announcement => ({ id, ...definition });

/** The keys recorded with the API for each part of an announcement. */
export const helpKeys = {
  announce: (id: string) => `${id}.announce`,
  tour: (id: string) => `${id}.tour`,
  hint: (id: string, anchor: HelpAnchor) => `${id}.hint.${anchor}`,
};
